(ns dashboard.components.paginated-list.view
  (:require [clojure.string :as str]
            [re-frame.core :as rf]
            ["react" :as react]
            [dashboard.components.paginated-list.events :as events]
            [dashboard.components.paginated-list.subs :as subs]
            [dashboard.components.column-visibility-popup :refer [column-visibility-popup]]
            [dashboard.components.expanding-textarea :refer [expanding-textarea]]
            [dashboard.components.paginated-list.config :refer [component-id]]
            [dashboard.utils :as utils]))

(defn SearchSyntaxInfo
  [on-close]
  [:div.search-info-modal.message
   [:h1.message-header "Search Syntax"
    [:button.delete {:on-click on-close}]]
   [:div.message-body
    [:div.search-tip
     [:span.tag.is-medium.is-info "Case-insensitive"]
     " : Seaching a word with " [:b "UPPERCASE"] " or " [:b "lowercase"]
     " letters will return results that match " [:b "either"] "." [:br]
     [:em "Example : "]
     [:span.tag.is-medium.is-success "CoMpLiAnCe"]
     " will return results that contain both "
     [:span.tag.is-medium.is-primary "COMPLIANCE"] " and " [:span.tag.is-medium.is-primary "compliance"]
     " as well as any variation of case between them."]
    [:div.search-tip
     [:span.tag.is-medium.is-info "<<(white)space>>"]
     " : A space between two words is treated as a logical AND. " [:br]
     [:em "Example : "] [:span.tag.is-medium.is-success "Industry Professional"]
     " will return results that match BOTH "
     [:span.tag.is-medium.is-primary "Industry"] [:b " AND "] [:span.tag.is-medium.is-primary "Professional"]]
    [:div.search-tip
     [:span.tag.is-medium.is-info "\"phrase containing several words\" [double quotes]"]
     " : Wrapping a phrase in double quotes will match results containing that " [:b "exact phrase"] "." [:br]
     [:em "Example : "] [:span.tag.is-medium.is-success "\"Leader in the field\""]
     " will return results that contain the phrase " [:span.tag.is-medium.is-primary "leader in the field"]]
    [:div.search-tip
     [:span.tag.is-medium.is-info "-"]
     " : adding a " [:span.tag.is-medium.is-info "-"]
     " before a single word or double-quotes phrase will return results that do NOT contain that term. (logical NOT)"
     [:br] [:em "Example 1 : "] [:span.tag.is-medium.is-success "-vendor"]
     " will return results that do NOT contain " [:span.tag.is-medium.is-primary "vendor"]
     [:br] [:em "Example 2 : "] [:span.tag.is-medium.is-success "-\"SOC2 certification\""]
     " will return results that do NOT contain the phrase " [:span.tag.is-medium.is-primary "SOC2 certification"]]]])

(defn LoadingMessage []
  [:div.paginated-list-searching
   [:span.icon.i.fas.fa-magnifying-glass]
   "Searching..."])

(defn column-id [column]
  (or (:id column) (:label column)))

(defn page-link
  "Render pagination page link under the paginated-list."
  [view-config-path current-page page-number]
  (let [label (str "Go to page " page-number)]
    [:li {:key page-number}
     [:a.pagination-link {:on-click (fn [_]
                                      (rf/dispatch [::events/set-page view-config-path page-number]))
                          :class [(when (= page-number current-page)
                                    "is-current")]
                          :aria-label label
                          :title label}
      page-number]]))

(defn Pagination [{:keys [view-config-path page pages prev? next?]}]
  (let [;; Pages available from 1 to N
        page-range         (range 1 (inc pages))]
    [:nav.pagination.mt-2 {:role "pagination" :aria-label "pagination"}
     [:button.pagination-previous.button.is-rounded
      {:disabled (not prev?)
       :on-click (fn [_]
                   (rf/dispatch [::events/set-prev-page view-config-path]))
       :title "Go to previous page"}
      "Previous"]
     [:button.pagination-next.button.is-rounded
      {:disabled (not next?)
       :on-click (fn [_]
                   (rf/dispatch [::events/set-next-page view-config-path]))
       :title "Go to next page"}
      "Next"]
     [:ul.pagination-list
      (if (> pages 5)
        [:<>
         (when (> page 2)
           [page-link view-config-path page 1])
         (when (= page 1)
           [:<>
            [page-link view-config-path page 1]
            [page-link view-config-path page 2]
            [page-link view-config-path page 3]])

         (when (> page 3)
           [:li>span.pagination-ellipsis "…"])

         (when (and (> page 1)
                    (< page (dec pages)))
           [:<>
            [page-link view-config-path page (dec page)]
            [page-link view-config-path page page]
            [page-link view-config-path page (inc page)]])

         (when (< page (- pages 2))
           [:li>span.pagination-ellipsis "…"])

         (when (>= page (dec pages))
           [:<>
            [page-link view-config-path page (- pages 2)]
            [page-link view-config-path page (- pages 1)]
            [page-link view-config-path page pages]])
         (when (< page (- pages 1))
           [page-link view-config-path page pages])]
        [:<>
         (map (fn [page-number]
                (page-link view-config-path page page-number))
              page-range)])]]))

(defn SelectAllRows [selected-row-indexes set-selected-row-indexes rows]
  (let [all-selected? (and (not= 0 (count rows))
                           (= (count selected-row-indexes) (count rows)))]
    [:input
     {:type "checkbox"
      :checked all-selected?
      :on-change (fn [_]
                   (if all-selected?
                     (set-selected-row-indexes #{})
                     (set-selected-row-indexes (into selected-row-indexes (range (count rows))))))}]))

(defn RowSelectionColumn [last-selected-row-index set-last-selected-row-index selected-row-indexes set-selected-row-indexes rows]
  {:label [SelectAllRows selected-row-indexes set-selected-row-indexes rows]
   :id :internal-row-selection-column
   :classes ["is-narrow"]
   :render (fn [{::keys [:dashboard.components.paginated-list.view/hide-row-selection]} idx]
             (when-not hide-row-selection
               [:input
                {:type "checkbox"
                 :default-checked (contains? selected-row-indexes idx)
                 :on-click (fn [e]
                             (set-last-selected-row-index idx)
                             (let [selected-row-indexes (if (contains? selected-row-indexes idx)
                                                          (disj selected-row-indexes idx)
                                                          (conj selected-row-indexes idx))
                                   selected-row-indexes (if (and (.-shiftKey e)
                                                                 last-selected-row-index)
                                                          (into selected-row-indexes (range (min last-selected-row-index idx) (max last-selected-row-index idx)))
                                                          selected-row-indexes)]
                               (set-selected-row-indexes selected-row-indexes)))}]))})

(defn on-pagesize-change [view-config-path e]
  (rf/dispatch [::events/set-pagesize view-config-path (js/parseInt (.. e -target -value))]))

(defn EditableRow [view-config-path editable-row columns on-save-editable-row editable-row-saving?]
  (let [;; Disable submit if there are any validatio errors
        [editable-row set-editable-row] (react/useState editable-row)
        disable-submit (seq (filter (fn [{:keys [editable-key editable-validator]}]
                                      (when editable-key
                                        (let [value (get editable-row editable-key)]
                                          (when editable-validator
                                            (not (editable-validator value))))))
                                    columns))]
    [:tr
     (doall
       (map-indexed
        (fn editable-column [idx {:keys [label
                                         classes
                                         editable-key
                                         editable-validator
                                         editable-format
                                         editable-disabled]
                                  :as column}]
          (let [id (column-id column)]
            [:td {:key (str "editable-row-column" idx)
                  :class (conj classes "is-vcentered")
                  :data-label label}
             (cond
               ;; Show submit control on :action column
               (= id :actions)
               [:span
                [:button.button.is-rounded.is-primary
                 {:on-click #(on-save-editable-row editable-row)
                  :class (cond
                           editable-row-saving? "is-loading"
                           disable-submit "is-disabled")
                  :disabled disable-submit
                  :title "Save row contents"}
                 "Save"]
                [:button.button.is-rounded.is-danger.is-light.ml-1
                 {:on-click #(do
                               (rf/dispatch [::events/update-state
                                             {:view-config-path view-config-path
                                              :updates {:editable-row nil}}]))
                  :title "Cancel edit"}
                 [:span.icon>i.fas.fa-xmark]]]

               ;; Show editor if we column is to be edited and editing isn't disabled
               (and editable-key (not editable-disabled))
               (let [value (get editable-row editable-key)]
                 ^{:key (str "editable-cell-" id)}
                 [:f> expanding-textarea
                  {:rows 1
                   :class (when (and editable-validator
                                     (not (editable-validator value)))
                            "is-danger")
                   :default-value value
                   :disabled editable-row-saving?
                   :on-change (fn [e]
                                (set-editable-row (assoc editable-row editable-key (.. e -target -value))))}])

               ;; If column participates in editing, but is disabled, then show value with formatting applied, if any
               (and editable-key editable-disabled)
               (let [value (get editable-row editable-key)]
                 (if editable-format
                   (editable-format value)
                   [:div value]))
               ;; Otherwise we have a column that doesn't participate in editing
               :else [:div])]))
         columns))]))

(defn PaginatedListHeader
  "Header for the Paginated List"
  [{:keys [columns
           header-content
           hide-pagination?
           hide-header?
           view-config-path
           pagination
           view-options]
    :as _view-props}]
  (let [{:keys [column-visibility column-popup-visible?]} view-options
        {:keys [page
                pagesize
                total]
         rows :items} pagination]
    (when-not hide-header?
      [:div.level
       [:div.level-left
        (when-not hide-pagination?
          [:div.level-item
           (let [end (+ (* (dec page) pagesize) (count rows))
                 begin (- end (dec (count rows)))]
             [:span "Showing  " begin "–" end " of " total])])]
       [:div.level-right.is-flex-shrink-1
        [:div.level-item.is-flex-shrink-1
         (when header-content
           (if (fn? header-content)
             [header-content]
             header-content))]
        [:div.level-item
         (when column-popup-visible?
           [:f> column-visibility-popup {:columns columns
                                         :column-visibility column-visibility
                                         :on-close #(rf/dispatch [::events/toggle-column-popup view-config-path])
                                         :on-column-visibility-changed (fn [column-id visible?]
                                                                         (rf/dispatch [::events/set-column-visible view-config-path column-id visible?]))}])
         [:button.button.is-rounded.is-secondary
          {:title "Change column visibility"
           :class (when (some #(= % false) (vals column-visibility))
                    "is-warning")
           :on-click (fn [e]
                       (.stopPropagation e)
                       (rf/dispatch [::events/toggle-column-popup view-config-path]))}
          [:span.icon>i.fas.fa-eye]]]
        (when-not hide-pagination?
          [:div.level-item
           [:select.input {:default-value pagesize
                           :on-change     #(on-pagesize-change view-config-path %)
                           :title         "Click to change how many rows to show per page"}
            [:option {:key 10 :value 10} 10]
            [:option {:key 15 :value 15} 15]
            [:option {:key 20 :value 20} 20]
            [:option {:key 1000 :value 1000} 1000]]])]])))

(defn PaginatedListSearch
  "Search Component for the Paginated List component"
  [{:keys [search-enabled
           search-extras
           on-search]}]
  (let [[search-info-visible set-search-info-visible] (react/useState false)
        [is-searching set-is-searching] (react/useState false)
        [visible-search-query set-visible-search-query] (react/useState nil)
        search-callback #(set-is-searching false)]
    (when search-enabled
      [:div {:style {:display "flex"
                     :align-items "center"}}
       (when is-searching [LoadingMessage])
       [:div.control.has-icons-left {:style {:width "100%"}}
        [:span.icon.is-left>i.fas.fa-magnifying-glass]
        [:input.input
         {:type "search"
          :title (str/join "\n" ["Type a query to filter the table"])
          :value visible-search-query
          :on-change (fn [e]
                       (let [search-query (.. e -target -value)]
                         (set-is-searching true)
                         (on-search search-query search-callback)
                         (set-visible-search-query search-query)))}]
        (when search-info-visible [SearchSyntaxInfo #(set-search-info-visible false)])]
       [:button.button.is-rounded.ml-2.is-info
        {:title "Search Syntax Information"
         :on-click (fn [_] (set-search-info-visible not))}
        [:span.icon>i.fas.fa-solid.fa-info]]
       (when search-extras [search-extras])])))

(defn PaginatedListTableHeader
  "Table Header for the Paginated List component"
  [{:keys [columns
           column-editors
           view-config-path
           pagination
           selected-row-indexes
           allow-tree-sort
           tree-sort-toggle-column-id
           view-options]
    :as _view-props}]
  (let [{:keys [sort-keyfn
                sort-order]
         rows :items} pagination
        {:keys [tree-sort]} view-options
        sort-icon-element (case sort-order
                            :asc :i.fas.fa-chevron-up
                            :desc :i.fas.fa-chevron-down
                            nil)
        selected-rows (keep (fn [[idx row]]
                              (when (contains? selected-row-indexes idx)
                                row))
                            (map-indexed vector rows))]
    [:thead>tr
     (map-indexed
       (fn column-header [index {column-sort-key :sort-key
                                 :keys [label id classes title]}]
         (let [current-sort? (= column-sort-key sort-keyfn)
               sort-title "Click to change the sort direction"
               base-updates {:sort-keyfn column-sort-key
                             :sort-order (cond
                                           ;; If sort key changes, we keep the existing order
                                           (not current-sort?) sort-order
                                           ;; If the sort key was the same, we change the order
                                           (= sort-order :desc) :asc
                                           :else :desc)
                             :tree-sort tree-sort}]
           [:th {:key   (str "column-" index)
                 :class classes}
            (if column-sort-key
              [:div.is-flex
               (when (and allow-tree-sort
                          (= id tree-sort-toggle-column-id))
                 [:div.is-flex
                  {:title "Toggle sorting rows according to tree structure. When checked, selecting a sort column sorts the siblings in a tree."}
                  [:input
                   {:type "checkbox"
                    :id "paginated-list-tree-sort"
                    :checked (:tree-sort view-options)
                    :on-change (fn [_]
                                 (rf/dispatch [::events/update-state
                                               {:view-config-path view-config-path
                                                :updates (update base-updates :tree-sort not)}]))}]
                  [:label
                   {:for "paginated-list-tree-sort"}
                   [:span.icon.mr-2
                    [:i.fas.fa-solid.fa-sitemap]]]])
               [:a
                {:on-click (fn [_]
                             (rf/dispatch [::events/update-state
                                           {:view-config-path view-config-path
                                            :updates base-updates}]))
                 :title (if title
                          (str title "\n\n" sort-title)
                          sort-title)}
                label]
               (when (and current-sort? sort-icon-element)
                 [:span.icon [sort-icon-element]])]
              [:span
               {:title title}
               label])
            (when-let [column-editor (and (seq selected-row-indexes)
                                          (column-editors id))]
              [:f> column-editor selected-rows])]))
       columns)]))

(defn PaginatedListTableBody
  "Table Body for the Paginated List component"
  [{:keys [columns
           row-props-transform
           cell-props-transform
           cell-classes
           pagination]
    :or {cell-classes ["is-vcentered"]}
    :as _view-props}]
  (let [{:keys [loading loading-sub]
         rows :items} pagination]
    (when-not (or (when loading-sub
                    @(rf/subscribe loading-sub))
                  loading)
      [:<>
       (doall
       (map-indexed
        (fn [idx-row {:paginated-list/keys [row-key] :as row}]
          (let [row-props {}]
            #^{:key row-key}
              [:tr (if row-props-transform
                     (row-props-transform row row-props)
                     row-props)
               (map (fn [{:keys [id] :as col}]
                      (let [{:keys [label render classes]} col
                            cell-props {:key   (str row-key id)
                                        :class (into classes cell-classes)
                                        :data-label (when (string? label)
                                                      label)}]
                        [:td (if cell-props-transform
                                       (cell-props-transform row cell-props)
                                       cell-props)
                                 [:f> render row idx-row]]))
                    columns)]))
        rows))])))


(defn PaginatedListPresentation
  "Overall presentation of the Paginated List"
  [{:keys [view-config-path
           class
           table-class
           table-style
           columns
           hide-pagination?
           column-editors
           pagination
           view-options
           on-save-editable-row]
    :or {table-class ["is-striped"]}
    :as props}]
  (let [[last-selected-row-index set-last-selected-row-index] (react/useState nil)
        [selected-row-indexes set-selected-row-indexes] (react/useState #{})

        {:keys [column-visibility
                editable-row
                editable-row-saving?]} view-options

        {:keys [page
                pages
                prev?
                next?
                loading
                loading-sub]
         rows :items} pagination

        column-filter (if editable-row
                        (constantly true)
                        (fn [column]
                          (let [id (column-id column)]
                            (get column-visibility id true))))]
    [:div {:class class}
     (when (or (when loading-sub
                 @(rf/subscribe loading-sub))
               loading)
       [:div.loader])
     [:f> PaginatedListHeader props]
     [:f> PaginatedListSearch props]
     [:div.b-table
      (let [columns (filter column-filter columns)
            columns (if (seq column-editors)
                      (conj (into [] columns)
                            (RowSelectionColumn last-selected-row-index
                                                set-last-selected-row-index
                                                selected-row-indexes
                                                set-selected-row-indexes
                                                rows))
                      columns)]
        [:div.table-wrapper
         [:table.table.is-fullwidth (cond-> {:class table-class}
                                      table-style (assoc :style table-style))
          ;; Add column group only if all columns have :col attribute
          (when (every? :col (remove #(= :internal-row-selection-column (:id %)) columns))
            [:colgroup
             (map-indexed (fn [idx column]
                            ^{:key idx}
                            [:col (:col column)])
                          columns)])
          [:f> PaginatedListTableHeader (assoc props
                                               :selected-row-indexes selected-row-indexes
                                               :columns columns)]
          [:tbody.paginated-table-body
           (when (and (empty? rows) (nil? editable-row))
             [:tr [:td {:colSpan (count columns)} "No items found"]])

           ;; Render editable row
           (when editable-row
             [:f> EditableRow view-config-path editable-row columns on-save-editable-row editable-row-saving?])

           ;; Map over each row and column.
           ;; Pick the values based on column config.
           ;; Render the column value with column/render.
           [:f> PaginatedListTableBody (assoc props :columns columns)]]]])]
     (when-not hide-pagination?
       [Pagination {:view-config-path view-config-path
                    :page page
                    :pages pages
                    :prev? prev?
                    :next? next?}])]))

(def default-pagesize 10)

(defn reverse-compare
  "Reverse compare, just to be able to name the comparator function, to help reading stack traces"
  [a b]
  (compare b a))

(defn paginate [view-options {:keys [data-filter data-transform initial-pagesize default-sort-keyfn default-sort-order hide-pagination?
                                items items-sub]}]
  (let [{:keys [page pagesize sort-keyfn sort-order search-query tree-sort]
         :or {pagesize (or initial-pagesize default-pagesize)
              page 1}} view-options

        sort-keyfn (or sort-keyfn default-sort-keyfn)
        sort-order (or sort-order default-sort-order)
        sort-comp (case sort-order
                    :asc compare
                    :desc reverse-compare
                    sort-order)
        pred (partial (or data-filter (constantly true)) search-query)

        items (if items-sub
                @(rf/subscribe items-sub)
                items)
        items (if data-transform
                (filter pred (data-transform items sort-keyfn sort-comp tree-sort))
                (filter pred (sort-by sort-keyfn sort-comp items)))
        skip (* pagesize (- page 1))
        pages (js/Math.ceil (/ (count items) pagesize))

        total (count items)
        items (if hide-pagination?
                items
                (->> items
                     (drop skip)
                     (take pagesize)))]
    (cond-> {:items items
             :sort-keyfn sort-keyfn
             :sort-order sort-order
             :total total}
      (not hide-pagination?) (assoc :prev? (> page 1)
                                    :next? (< page pages)
                                    :page page
                                    :pages pages
                                    :pagesize pagesize))))

(defn PaginatedList [{:keys [config data-props]}]
  (assert (map? config))
  (assert (map? data-props))
  (assert (every? :id (:columns config))
          "Provide :id for each column")
  (when-not (:data-transform data-props)
    (when (:items data-props)
      (assert (every? :paginated-list/row-key (:items data-props))
              "Provide :paginated-list/row-key for each row"))
    (when (:items-sub data-props)
      (assert (every? :paginated-list/row-key @(rf/subscribe (:items-sub data-props)))
              "Provide :paginated-list/row-key for each row")))
  (when-let [data-transform (:data-transform data-props)]
    (assert (every? :paginated-list/row-key (data-transform (or (:items data-props)
                                                                @(rf/subscribe (:items-sub data-props)))
                                                            (:default-sort-keyfn data-props)
                                                            compare))
            "Provide :paginated-list/row-key for each row via data-transform"))
  (let [{:keys [view-config-path view-name hide-pagination? default-view-options]} config
        view-config-path (or view-config-path [view-name component-id])
        on-search (utils/debounce
                   (fn [search-query callback-fn]
                     (rf/dispatch [::events/update-state
                                   {:view-config-path view-config-path
                                    :updates {:search-query search-query
                                              :page 1}}])
                     (callback-fn))
                   740)]
    (fn [{:keys [config data-props]}]
      (react/useEffect (fn []
                         (fn []
                           (rf/dispatch [::events/update-state {:view-config-path view-config-path
                                                                :updates {:search-query nil}}])))
                       (array []))
      (let [view-options @(rf/subscribe [::subs/view-options view-config-path default-view-options])]
        [:f> PaginatedListPresentation
         (assoc config
                :view-config-path view-config-path
                :pagination (assoc (paginate view-options
                                             (assoc data-props
                                                    :hide-pagination? hide-pagination?))
                                   :loading (:loading data-props)
                                   :loading-sub (:loading-sub data-props))
                :view-options view-options
                :on-search on-search)]))))

(defn paginated-list [config data-props]
  [:f> PaginatedList {:config config
                      :data-props data-props}])
